import { useState, useEffect, useRef } from "react";
import {useSelector} from 'react-redux';
import {getCampaign, getResponsesLive, setLiveControls, getLiveControls} from '../../../firebaseActions.mjs';
import TimerControl from "./TimerControl";
import PageSelectDropdown from "./PageSelectDropdown";

const getNames = async (userIds) => {
	const get = userIds
	.map((id)=>id.replaceAll(':',''))
	.join(",");
	const response = await fetch("https://conferience.com/custom/DEVELOPMENTDAY/getUsers.php?u=" + get)
	const dt = await response.json();
	return dt;
}
const stripHtml = (html) => {
	if(!html) return "";
	const div = document.createElement("div");
	div.innerHTML = html;
	return (div.textContent || "").trim();
};
const getPageLabel = (page) => {
	const sE = page.elements.find((e) => e.type === "multipleChoice" || e.type === "poll" || e.type === "imageChoice");
	const sT = !sE && page.elements.find((e) => e.type === "text");
	const text = stripHtml(sE?.question || sT?.html || "");
	const truncated = text.length > 40 ? text.slice(0, 40) + "…" : text;
	return [sE?"quiz":"description", truncated]
};
const toMillis = (timestamp)=>timestamp.seconds * 1000 + Math.floor(timestamp.nanoseconds / 1e6);
function CampaignControl(){
	const {currentCampaignId} = useSelector(state => state.user);
	const [nextTimer, setNextTimer] = useState(null);
	const [pages, setPages] = useState([]);
	const [controlData, setControlData] = useState(null);
	const [sidebarOpen, setSidebarOpen] = useState(false);
	const [graphData, setGraphData] = useState({});
	const [leaderboardData, setLeaderboardData] = useState(null);
	const [names, setNames] = useState({});
	// Kept in a ref so handlers (e.g. the "log winners" button) can read the
	// latest accumulated answers without depending on a re-render.
	// Shape: Map<elementId, Map<userId, answerObject>>
	const elementAnswersMapRef = useRef(new Map());

	const [controlBarVisible, setControlBarVisible] = useState(false);
	// Force the use of the dark theme
	useEffect(() => {
		document.querySelector("html").setAttribute("data-theme", "dark");
	}, []);

	useEffect(() => {
		const REVEAL_ZONE_PX = 120;
		const handleMouseMove = (e) => {
			setControlBarVisible(window.innerHeight - e.clientY <= REVEAL_ZONE_PX);
		};
		window.addEventListener('mousemove', handleMouseMove);
		return () => window.removeEventListener('mousemove', handleMouseMove);
	}, []);

	useEffect(()=>{
		let cancelled = false;
		setControlData(null);
		getCampaign(currentCampaignId)
		.then( (data) => {
			if(cancelled) return;
			setPages(data.campaign.pages)
		})
		.catch((e) => {
			alert('Could not load campaign.');
			console.error(e, { ...e });
		})
		let unsub;
		if(currentCampaignId){
			unsub = getLiveControls(currentCampaignId, (controlData)=>{
				setControlData(controlData)
			});
		}
		return () => {
			cancelled = true;
			if(unsub) unsub();
		}
	}, [currentCampaignId]);

	useEffect(() => {
		let timeoutId;
		if(currentCampaignId && controlData?.lockTime && !controlData.timedOut){
			timeoutId = setTimeout(()=>{
				updateControl({timedOut: true, lockTime: null});
			}, controlData.lockTime - Date.now());
		}
		return () => timeoutId&&clearTimeout(timeoutId);
	}, [controlData, currentCampaignId]);

	useEffect(() => {
		const handleKeyDown = (e) => {
			if(e.ctrlKey || e.metaKey || e.altKey) return;
			const t = e.target;
			const tag = t?.tagName;
			if(tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || t?.isContentEditable) return;

			const key = e.key;
			if(key === 'm' || key === 'M' || key === 'μ' || key === 'Μ'){
				setSidebarOpen((prev) => !prev);
			} else if(key === 'g' || key === 'G' || key === 'γ' || key === 'Γ'){
				if(controlData){
					updateControl({ showResults: !controlData.showResults });
				}
			} else if(key === 'p' || key === 'P' || key === 'π' || key === 'Π'){
				if(isPaused) handlePlay();
				else handlePause();
			} else if(key === 'l' || key === 'L' || key === 'λ' || key === 'Λ'){
				if(!controlData?.showingLeaderboard) updateControl({showingLeaderboard:true});
				else updateControl({showingLeaderboard:false});
			} else if(key === 'c' || key === 'C' || key === 'ψ' || key === 'Ψ'){
				if(controlData){
					updateControl({ showCorrect: !controlData.showCorrect });
				}
			} else if(key === 'ArrowLeft'){
				if(canPrev){
					e.preventDefault();
					handlePrev();
				}
			} else if(key === 'ArrowRight'){
				if(canNext){
					e.preventDefault();
					handleNext();
				}
			}
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [controlData, pages, nextTimer]);

	useEffect(() => {
		if(controlData?.showingLeaderboard) handleShowLeaderboard();
		else setLeaderboardData(null);
	}, [controlData?.showingLeaderboard]);
	useEffect(() => {
		// Reset stats whenever the campaign changes
		setGraphData({});
		elementAnswersMapRef.current = new Map();

		if(!currentCampaignId) return;

		// Per-campaign accumulator: elementId -> (userId -> answerObject)
		const elementAnswersMap = elementAnswersMapRef.current;

		const calcStats = () => {
			const newGraphData = {};
			for (const [elementId, elementUserAnswers] of elementAnswersMap.entries()) {
				newGraphData[elementId] = {options:{}, totalAnswers:0};
				for (const [userId, answerObject] of elementUserAnswers.entries()) {
					if (!Array.isArray(answerObject.value)) continue;
					newGraphData[elementId].totalAnswers++;
					answerObject.value.forEach((optionId)=>{
						if(!newGraphData[elementId].options[optionId]) newGraphData[elementId].options[optionId] = {count:0, perc:0};
						newGraphData[elementId].options[optionId].count++;
					});
				}
				// calc percentages
				for(const optionId in newGraphData[elementId].options){
					let perc = 0;
					if(newGraphData[elementId].totalAnswers){
						perc = newGraphData[elementId].options[optionId].count/newGraphData[elementId].totalAnswers;
					}
					newGraphData[elementId].options[optionId].perc = perc;
				}
			}
			setGraphData(newGraphData);
		};

		const handleVotesUpdate = (userId, data) => {
			for(const pageId in data){
				for(const elementId in data[pageId].elements){
					if(!elementAnswersMap.has(elementId)) elementAnswersMap.set(elementId, new Map());
					elementAnswersMap.get(elementId).set(userId, data[pageId].elements[elementId]);
				}
			}
			calcStats();
		};

		const handleVotesDeletion = (userId) => {
			for (const elementUserAnswers of elementAnswersMap.values()) {
				elementUserAnswers.delete(userId);
			}
			calcStats();
		};

		const unsubPromise = getResponsesLive(
			currentCampaignId,
			handleVotesUpdate, // added
			handleVotesUpdate, // modified
			handleVotesDeletion // removed
		);

		return () => {
			if(unsubPromise) unsubPromise.then((unsub)=>unsub?.());
		};
	}, [currentCampaignId]);

	// Build ordered navigation list, ignoring participated / timed-out / pause-screen pages
	const excludedIds = new Set([
		controlData?.participatedPageId,
		controlData?.elapsedPageId,
		controlData?.startPageId,
	].filter(Boolean));
	const orderedPages = pages.filter(p => !excludedIds.has(p.id));
	const currentOrderedIndex = orderedPages.findIndex(p => p.id === controlData?.latestOpenPageId);
	const currentPage = orderedPages[currentOrderedIndex] ?? null;

	const currentPageQuestion = currentPage?.elements.find((e)=>e.type==="multipleChoice"||e.type==="poll"||e.type==="imageChoice");
	const currentPageTitle = currentPage && (currentPageQuestion?.question || currentPage.elements.find((e)=>e.type==="text")?.html);

	const currentStats = currentPageQuestion ? graphData[currentPageQuestion.id] : null;
	const currentTotal = currentStats?.totalAnswers || 0;

	const updateControl = (patch, forcePause=false) => {
		const o = { ...controlData, ...patch };
		if(patch.pageId && orderedPages.find(p=>p.id===patch.pageId)) {
			o.latestOpenPageId = patch.pageId;
			// If a pause page exists force the use of it
			if(forcePause && controlData?.startPageId){
				o.pageId = controlData.startPageId
				o.timedOut = false;
				o.lockTime = null;
			}
		}
		setLiveControls(currentCampaignId, o)
	};
	const handleGo = (page, isStartPage, isEndPage, forcePause=false) => {
		const shouldLock = !isStartPage && !isEndPage && nextTimer!==null;
		updateControl({
			showingLeaderboard: false,
			timedOut: false,
			showCorrect: false,
			pageId: page.id,
			lockTime: shouldLock ? Date.now() + nextTimer * 1000 : null,
		}, forcePause);
	};
	const lastPageId = pages[pages.length-1]?.id;

	const goToOrderedPage = (idx, forcePause=false) => {
		const page = orderedPages[idx];
		if(!page) return;
		const isEndPage = page.id === lastPageId;
		handleGo(page, false, isEndPage, forcePause);
	};
	const handlePrev = () => {
		if(currentOrderedIndex > 0) goToOrderedPage(currentOrderedIndex - 1, true);
	};
	const handleNext = () => {
		if(currentOrderedIndex < 0 && orderedPages.length > 0){
			goToOrderedPage(0, true);
		}else if(currentOrderedIndex >= 0 && currentOrderedIndex < orderedPages.length - 1){
			goToOrderedPage(currentOrderedIndex + 1, true);
		}
	};
	const handlePlay = () => {
		if(currentOrderedIndex < 0 && orderedPages.length > 0){
			goToOrderedPage(0);
		}else if(currentOrderedIndex >= 0 && currentOrderedIndex < orderedPages.length){
			goToOrderedPage(currentOrderedIndex);
		}
	};
	const handlePause = () => {
		if(!controlData || controlData.showingLeaderboard) return;
		updateControl({timedOut: false, lockTime: null, showCorrect: false, pageId: controlData.startPageId || controlData.pageId});
	};

	const handleShowLeaderboard = () => {
		const CORRECT_ANSWER_POINTS = 1000;
		const TIME_BONUS_POINTS = 150;
		const MAX_BONUS_TIME = 30_000;
		// 1. Collect every multipleChoice element that has at least one correct option.
		const quizQuestions = [];
		pages.forEach((page) => {
			page.elements.forEach((el) => {
				if(el.type === "multipleChoice" && Array.isArray(el.options) && el.options.some((o) => o.isCorrect)){
					quizQuestions.push(el);
				}
			});
		});

		if(quizQuestions.length === 0){
			console.log("No multiple choice questions with correct answers in this campaign.");
			return;
		}

		// 2. Gather every userId we've seen respond to anything and map the fastest times.
		const allUserIds = new Set();
		const minTimes = new Map();
		for(const [elementId, userAnswers] of elementAnswersMapRef.current.entries()){
			for(const [userId, ans] of userAnswers.entries()){
				const t = toMillis(ans.timestamp);
				if(!minTimes.has(elementId)) minTimes.set(elementId, t);
				else if(minTimes.get(elementId)>t) minTimes.set(elementId, t);
				allUserIds.add(userId);
			}
		}

		// 3. Pre-compute the expected correct-option set for each quiz question.
		const expectedByQuestion = quizQuestions.map((q) => ({
			id: q.id,
			correctIds: new Set(q.options.filter((o) => o.isCorrect).map((o) => o.id)),
		}))

		// 4. Count how many quiz questions each user answered correctly.
		const correctUsers = [];
		for(const userId of allUserIds){
			let correctCount = 0;
			let points = 0;
			const answerTimes = {};
			for (const { id: qId, correctIds } of expectedByQuestion) {
				const answer = elementAnswersMapRef.current.get(qId)?.get(userId);
				if(!answer || !Array.isArray(answer.value)) continue;

				const selected = new Set(answer.value);
				if(selected.size !== correctIds.size) continue;

				let matches = true;
				for(const cid of correctIds){
					if(!selected.has(cid)){ matches = false; break; }
				}
				if(!matches) continue;

				// User answered this question correctly
				correctCount++;
				points += CORRECT_ANSWER_POINTS;
				if(answer.timestamp){
					const ms = toMillis(answer.timestamp);
					const elapsed = ms - minTimes.get(qId);
					const bonusTime = Math.max(MAX_BONUS_TIME-elapsed, 0);
					const bonusMultiplier = bonusTime/MAX_BONUS_TIME;
					points+=bonusMultiplier*TIME_BONUS_POINTS;
					answerTimes[qId] = ms;
				}
			}
			if(correctCount > 0) {
				correctUsers.push({
					userId,
					points,
					correctCount,
					answerTimes,
				});
			}
		}

		// 5. Calculate times (only across questions the user got correct)
		correctUsers.forEach((u)=>{
			let totalTime = 0;
			for(const qId in u.answerTimes){
				totalTime += u.answerTimes[qId];// - expectedByQuestion.get(qId).minTime;
			}
			u.totalTime = totalTime;
		});

		// 6. Sort by correctCount desc, totalTime asc as tie-breaker, take top 20
		correctUsers.sort((a, b) => b.points - a.points || a.totalTime - b.totalTime);
		const top = correctUsers.slice(0, 20);
		setLeaderboardData(top);
		setSidebarOpen(false);

		// Fetch display names for the shown users
		const userIds = top.map((u) => u.userId);
		if(userIds.length){
			getNames(userIds)
			.then((fetched) => setNames((prev) => ({ ...prev, ...fetched })))
			.catch((e) => console.error('Could not load user names', e));
		}
	};

	const canPrev = currentOrderedIndex > 0;
	const canNext = (currentOrderedIndex >= 0 && currentOrderedIndex < orderedPages.length - 1)
		|| (currentOrderedIndex < 0 && orderedPages.length > 0);
	const isPaused = !!controlData?.timedOut || (controlData?.pageId===controlData?.startPageId);

	return (<div className="campaign__control">
		{sidebarOpen && (
			<div className="campaign__controlSidebar">
				<div className="campaign__controlSidebar-header">
					<h4>Controls</h4>
					<span
						className="material-icons pointer"
						onClick={() => setSidebarOpen(false)}
						title="Close (M)"
					>close</span>
				</div>
				<div className="campaign__controlSidebar-body">
					<TimerControl value={nextTimer} onChange={setNextTimer} />
					<div className="mtop--15">
						<PageSelectDropdown
							label="Participated page"
							pages={pages}
							selectedId={controlData?.participatedPageId}
							onSelect={(id) => updateControl({ participatedPageId: id })}
						/>
					</div>
					<div className="mtop--10">
						<PageSelectDropdown
							label="Timed out page"
							pages={pages}
							selectedId={controlData?.elapsedPageId}
							onSelect={(id) => updateControl({ elapsedPageId: id })}
						/>
					</div>
					<div className="mtop--10">
						<PageSelectDropdown
							label="Pause screen"
							pages={pages}
							selectedId={controlData?.startPageId}
							onSelect={(id) => updateControl({ startPageId: id })}
						/>
					</div>
					<div className="mtop--15">
						<button
							type="button"
							className="btn btn-primary-outline btn-100"
							onClick={() => updateControl({showingLeaderboard: true})}
							title="Show the leaderboard of users who answered every quiz question correctly"
						>
							<span className="material-icons mright--5">emoji_events</span>
							Show leaderboard
						</button>
					</div>
					<table>
						<tbody>
							{pages.map((page)=>{
								const [icon, label] = getPageLabel(page);
								const isCurrent = page.id === controlData?.pageId;
								const isParticipatedPage = page.id === controlData?.participatedPageId;
								const isElapsedPage = page.id === controlData?.elapsedPageId;
								const isStartPage = page.id === controlData?.startPageId;
								const isEndPage = page.id === lastPageId;

								let textColor = null;
								if(isCurrent) textColor = "var(--primary)";
								if(isParticipatedPage) textColor = "var(--success)";
								if(isElapsedPage) textColor = "var(--danger)";
								const sE = page.elements.find((e)=>e.type==="multipleChoice"||e.type==="poll"||e.type==="imageChoice");
								const sT = !sE && page.elements.find((e)=>e.type==="text");
								return <tr key={page.id}>
									<td style={{"width": "50px", "color":textColor}}>
										<span className="material-icons" style={{"fontSize":(isCurrent?"35px":"20px")}}>
											{icon}
										</span>
									</td>
									<td>
										<span>{label}</span>
									</td>
									<td>
										{!isCurrent&&!isParticipatedPage&&!isElapsedPage&&<button onClick={()=>handleGo(page, isStartPage, isEndPage)} type="button">Go!</button>}
										</td>
									</tr>
								})}
						</tbody>
					</table>
					<div className="mtop--20" style={{borderTop: '1px solid RGBA(var(--textColor-rgb) / 10%)', paddingTop: '15px'}}>
						<div className="text--muted font--12 text--bold mbottom--10" style={{textTransform: 'uppercase', letterSpacing: '0.5px'}}>Shortcuts</div>
						{[
							{keys: ['M'], label: 'Toggle this sidebar'},
							{keys: ['G'], label: 'Toggle results'},
							{keys: ['C'], label: 'Mark correct answer'},
							{keys: ['P'], label: 'Play / Pause'},
							{keys: ['L'], label: 'Leaderboard'},
							{keys: ['←'], label: 'Previous question'},
							{keys: ['→'], label: 'Next question'},
						].map(({keys, label}) => (
							<div key={label} className="flex justify--spread mbottom--5" style={{alignItems: 'center'}}>
								<span className="font--13">{label}</span>
								<span>
									{keys.map((k) => (
										<kbd key={k} style={{
											display: 'inline-block',
											minWidth: '24px',
											padding: '2px 8px',
											marginLeft: '3px',
											textAlign: 'center',
											border: '1px solid RGBA(var(--textColor-rgb) / 20%)',
											borderBottomWidth: '2px',
											borderRadius: '4px',
											background: 'var(--secondary)',
											fontFamily: 'monospace',
											fontSize: '12px',
											fontWeight: 'bold'
										}}>{k}</kbd>
									))}
								</span>
							</div>
						))}
					</div>
				</div>
			</div>
		)}
		<div className="card card--100">
			{currentPage
				? <h3 dangerouslySetInnerHTML={{__html: currentPageTitle}} />
				: <h3 className="text--muted">No active page</h3>
			}
			{currentPageQuestion && (
				<div className="options--container">
					{currentPageQuestion.options.map((option) => {
						const optionStats = currentStats?.options?.[option.id];
						const perc = optionStats?.perc || 0;
						const count = optionStats?.count || 0;
						const percLabel = Math.round(perc * 100);
						const showResults = !!controlData?.showResults;
						const isCorrect = !!option.isCorrect;
						const markCorrect = !!controlData?.showCorrect && isCorrect;
						return (
							<div key={option.id} className="option--container">
								<div className="flex justify--spread">
									<span>
										{option.image&&<img src={option.image} className="img__responsive" style={{maxHeight:'100px'}}/>}
										{!option.image && option.text}
										{showResults && markCorrect && (
											<span className="material-icons text--success mleft--5" style={{fontSize: '18px'}} title="Correct answer">check_circle</span>
										)}
									</span>
									{showResults && (
										<span className="option--percent">{percLabel}%</span>
									)}
								</div>
								{showResults && (
									<div style={{
										height: '24px',
										background: 'RGBA(var(--textColor-rgb) / 10%)',
										borderRadius: '4px',
										marginTop: '6px',
										overflow: 'hidden'
									}}>
										<div style={{
											height: '100%',
											width: `${percLabel}%`,
											background: markCorrect ? 'var(--success)' : 'var(--textColor)',
											transition: 'width 0.3s ease'
										}} />
									</div>
								)}
							</div>
						);
					})}
				</div>
			)}
		</div>
		{currentPageQuestion && (
			<div className={`campaign__answerCount ${(controlBarVisible || sidebarOpen) ? 'campaign__answerCount-hidden' : ''}`}>
				<span className="material-icons">people</span>
				<span>{currentTotal}</span>
			</div>
		)}
		{leaderboardData && (
			<div className="campaign__leaderboard">
				<div className="campaign__leaderboard-header">
					<div className="flex" style={{alignItems: 'center', gap: '12px'}}>
						<span className="material-icons" style={{fontSize: '36px'}}>emoji_events</span>
						<h2>Leaderboard</h2>
					</div>
					<span onClick={() => updateControl({showingLeaderboard:false})} className="material-icons pointer" style={{fontSize: '32px'}} title="Close">close</span>
				</div>
				<div className="campaign__leaderboard-body">
					{leaderboardData.length === 0 ? (
						<div className="text--center text--muted font--20 mtop--35">
							No quiz answers yet.
						</div>
						) : (
						<ol className="campaign__leaderboard-list">
							{leaderboardData.map((user, i) => (
								<li
									key={user.userId}
									className={`campaign__leaderboard-item ${i < 3 ? `campaign__leaderboard-item--top${i + 1}` : ''}`}
									>
										<div className="campaign__leaderboard-rank">{i + 1}</div>
										<div className="campaign__leaderboard-name" title={user.userId}>
											{names[user.userId.replaceAll(":", "")] || user.userId}
										</div>
										<div className="campaign__leaderboard-score">
											{Math.round(user.points)}
										</div>
									</li>
									))}
						</ol>)
					}
				</div>
			</div>)}
		<div className={`campaign__controlBar ${(controlBarVisible || sidebarOpen) ? 'campaign__controlBar-visible' : ''}`}>
			<div className="campaign__controlBar-status">
				<span className={`pulse__blob ${isPaused ? 'pulse__blob-danger' : ''}`}></span>
				<span>{isPaused ? "Paused" : "Running"}</span>
			</div>
			<div className="campaign__controlBar-actions">
				<button
					type="button"
					className="btn btn-secondary"
					disabled={!canPrev}
					onClick={handlePrev}
					title="Previous page"
				>
					<span className="material-icons">skip_previous</span>
				</button>
				{isPaused ? (
					<button
						type="button"
						className="btn btn-primary"
						onClick={handlePlay}
						disabled={!canNext}
						title="Resume"
					>
						<span className="material-icons">play_arrow</span>
						<span className="mleft--5">Play</span>
					</button>
				) : (
					<button
						type="button"
						className="btn btn-warning"
						onClick={handlePause}
						title="Pause"
					>
						<span className="material-icons">pause</span>
						<span className="mleft--5">Pause</span>
					</button>
				)}
				<button
					type="button"
					className={`btn ${controlData?.showResults ? 'btn-primary' : 'btn-secondary'}`}
					onClick={() => updateControl({ showResults: !controlData?.showResults })}
					title={controlData?.showResults ? "Hide results" : "Show results"}
				>
					<span className="material-icons">
						{controlData?.showResults ? 'visibility' : 'visibility_off'}
					</span>
				</button>
				<button
					type="button"
					className="btn btn-secondary"
					disabled={!canNext}
					onClick={handleNext}
					title="Next page"
				>
					<span className="material-icons">skip_next</span>
				</button>
			</div>
			<div className="campaign__controlBar-position">
				{orderedPages.length > 0 && (
					currentOrderedIndex >= 0
						? `Page ${currentOrderedIndex + 1} of ${orderedPages.length}`
						: `— of ${orderedPages.length}`
				)}
			</div>
		</div>
	</div>);
}
export default CampaignControl;
